// src/services/TransactionMonitoringService.js
import auditService from './AuditService';
import moduleFreshnessService from './ModuleFreshnessService';
import storage from './StorageAdapter';
import entityService from './EntityService';
import settingsService from './SettingsService';
import { initialRisks } from '../modules/BWRA/data/riskData';
import txmonApi from '../api/txmon';
import { makeDualWriteMixin, applyDualWriteMixin } from './_dualWriteHelpers';

class TransactionMonitoringService {
  constructor() {
    this.RULES_KEY = 'txmon_rules';
    this.RULE_VERSIONS_KEY = 'txmon_rule_versions';
    this.RULE_VERSIONS_LIMIT = 50;
    this.THRESHOLDS_KEY = 'txmon_thresholds';
    this.ALERT_PERFORMANCE_KEY = 'txmon_alert_performance';
    this.MODELS_KEY = 'txmon_models';
    this.VALIDATION_KEY = 'txmon_validations';
    this.ALERT_CASES_KEY = 'txmon_alert_cases';
    this.listeners = [];

    if (typeof window !== 'undefined') {
      window.addEventListener('rms_active_entity_changed', () => {
        this.notifyListeners();
      });
    }
  }

  _entityKey(baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${baseKey}_${entityId}` : baseKey;
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(l => l());
  }

  // === RULES ===

  getRules() {
    return storage.getJSON(this._entityKey(this.RULES_KEY), []);
  }

  getRule(id) {
    return this.getRules().find(r => r.id === id) || null;
  }

  async addRule(rule) {
    const rules = this.getRules();
    const newRule = {
      ...rule,
      id: rule.id || this.generateRuleId(),
      enabled: rule.enabled !== undefined ? rule.enabled : true,
      declaredRisks: rule.declaredRisks || [],
      linkedProducts: rule.linkedProducts || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    rules.push(newRule);
    await this._dualWriteJson(this.RULES_KEY, rules);
    await moduleFreshnessService.markModified('TransactionMonitoring');
    auditService.log('CREATE', 'TransactionMonitoring', { entity: 'Rule', name: newRule.name, id: newRule.id });
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('ruleChanged', { action: 'created', ruleId: newRule.id, ruleName: newRule.name });
    } catch (_e) { /* best-effort */ }
    this.notifyListeners();
    return newRule;
  }

  async updateRule(id, updates) {
    const rules = this.getRules();
    const idx = rules.findIndex(r => r.id === id);
    if (idx < 0) return null;
    await this._saveRuleVersion(rules[idx]);
    rules[idx] = {
      ...rules[idx],
      ...updates,
      id: rules[idx].id,
      createdAt: rules[idx].createdAt,
      updatedAt: new Date().toISOString(),
    };
    await this._dualWriteJson(this.RULES_KEY, rules);
    await moduleFreshnessService.markModified('TransactionMonitoring');
    auditService.log('UPDATE', 'TransactionMonitoring', { entity: 'Rule', name: rules[idx].name, id });
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('ruleChanged', { action: 'updated', ruleId: id, ruleName: rules[idx].name });
    } catch (_e) { /* best-effort */ }
    this.notifyListeners();
    return rules[idx];
  }

  async deleteRule(id) {
    const rule = this.getRule(id);
    const rules = this.getRules().filter(r => r.id !== id);
    await this._dualWriteJson(this.RULES_KEY, rules);
    await moduleFreshnessService.markModified('TransactionMonitoring');
    if (rule) {
      auditService.log('DELETE', 'TransactionMonitoring', { entity: 'Rule', name: rule.name, id });
    }
    try {
      // eslint-disable-next-line
      const ns = require('./NotificationService').default;
      ns.emit('ruleChanged', { action: 'deleted', ruleId: id, ruleName: rule?.name });
    } catch (_e) { /* best-effort */ }
    this.notifyListeners();
  }

  async batchUpdateRules(ids, updates) {
    const rules = this.getRules();
    let updatedCount = 0;
    const batchId = `BATCH-${Date.now()}`;
    for (const id of ids) {
      const idx = rules.findIndex(r => r.id === id);
      if (idx < 0) continue;
      await this._saveRuleVersion(rules[idx], { batchId });
      rules[idx] = { ...rules[idx], ...updates, id: rules[idx].id, updatedAt: new Date().toISOString() };
      updatedCount++;
    }
    await this._dualWriteJson(this.RULES_KEY, rules);
    await moduleFreshnessService.markModified('TransactionMonitoring');
    auditService.log('BATCH_UPDATE', 'TransactionMonitoring', { entity: 'Rule', ids, updates });
    if (updatedCount > 0) {
      try {
        // eslint-disable-next-line
        const ns = require('./NotificationService').default;
        ns.emit('ruleChanged', { action: 'batchUpdated', count: updatedCount, batchId });
      } catch (_e) { /* best-effort */ }
    }
    this.notifyListeners();
    return updatedCount;
  }

  generateRuleId() {
    const rules = this.getRules();
    const maxNum = rules.reduce((max, r) => {
      const match = r.id && r.id.match(/^TXR-(\d+)$/);
      const num = match ? parseInt(match[1], 10) : 0;
      return num > max ? num : max;
    }, 0);
    return `TXR-${String(maxNum + 1).padStart(3, '0')}`;
  }

  // === RULE VERSION HISTORY ===

  async _saveRuleVersion(rule, options = {}) {
    const all = storage.getJSON(this._entityKey(this.RULE_VERSIONS_KEY), []);
    const ruleVersions = all.filter(v => v.ruleId === rule.id);
    const versionNumber = ruleVersions.length + 1;
    const user = auditService.getCurrentUser();
    const entry = {
      id: `VER-${rule.id}-${Date.now()}`,
      ruleId: rule.id,
      version: versionNumber,
      snapshot: { ...rule },
      changedAt: new Date().toISOString(),
      changedBy: user.username || 'unknown',
      ...(options.batchId ? { batchId: options.batchId, batchOperation: true } : {}),
    };
    all.unshift(entry);
    // Keep only the last N versions total (across all rules)
    await this._dualWriteJson(this.RULE_VERSIONS_KEY, all.slice(0, this.RULE_VERSIONS_LIMIT * 10));
  }

  getRuleVersions(ruleId) {
    const all = storage.getJSON(this._entityKey(this.RULE_VERSIONS_KEY), []);
    return all.filter(v => v.ruleId === ruleId);
  }

  async restoreRuleVersion(ruleId, versionId) {
    const versions = this.getRuleVersions(ruleId);
    const version = versions.find(v => v.id === versionId);
    if (!version) return null;
    const { id, createdAt, ...restorableFields } = version.snapshot;
    return this.updateRule(ruleId, restorableFields);
  }

  // Returns { riskId: [{ id, name }] } — which BWRA risks each rule declares it addresses
  getDeclaredRiskCoverage() {
    const rules = this.getRules();
    const coverage = {};
    rules.forEach(rule => {
      const risks = rule.declaredRisks || [];
      risks.forEach(riskId => {
        if (!coverage[riskId]) coverage[riskId] = [];
        coverage[riskId].push({ id: rule.id, name: rule.name });
      });
    });
    return coverage;
  }

  // Returns { categoryId: [{ id, name, enabled }] } — which BWRA categories each rule declares it covers
  getAllRuleCoverage() {
    const rules = this.getRules();
    const coverage = {};
    rules.forEach(rule => {
      const addEntry = (catId) => {
        if (!coverage[catId]) coverage[catId] = [];
        if (!coverage[catId].find(x => x.id === rule.id)) {
          coverage[catId].push({ id: rule.id, name: rule.name, enabled: rule.enabled });
        }
      };
      const declaredRisks = rule.declaredRisks || [];
      declaredRisks.forEach(riskId => {
        const risk = initialRisks.find(r => r.id === riskId);
        if (risk?.category) addEntry(risk.category);
      });
    });
    return coverage;
  }

  // === THRESHOLDS ===

  getThresholds() {
    return storage.getJSON(this._entityKey(this.THRESHOLDS_KEY), []);
  }

  getThreshold(id) {
    return this.getThresholds().find(t => t.id === id) || null;
  }

  generateThresholdId() {
    const thresholds = this.getThresholds();
    const maxNum = thresholds.reduce((max, t) => {
      const match = t.id && t.id.match(/^THR-(\d+)$/);
      const num = match ? parseInt(match[1], 10) : 0;
      return num > max ? num : max;
    }, 0);
    return `THR-${String(maxNum + 1).padStart(3, '0')}`;
  }

  async addThreshold(threshold) {
    const thresholds = this.getThresholds();
    const newThreshold = {
      ...threshold,
      id: threshold.id || this.generateThresholdId(),
      enabled: threshold.enabled !== undefined ? threshold.enabled : true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    thresholds.push(newThreshold);
    await this._dualWriteJson(this.THRESHOLDS_KEY, thresholds);
    auditService.log('CREATE', 'TransactionMonitoring', { entity: 'Threshold', name: newThreshold.name, id: newThreshold.id });
    this.notifyListeners();
    return newThreshold;
  }

  async updateThreshold(id, updates) {
    const thresholds = this.getThresholds();
    const idx = thresholds.findIndex(t => t.id === id);
    if (idx < 0) return null;
    thresholds[idx] = {
      ...thresholds[idx],
      ...updates,
      id: thresholds[idx].id,
      createdAt: thresholds[idx].createdAt,
      updatedAt: new Date().toISOString(),
    };
    await this._dualWriteJson(this.THRESHOLDS_KEY, thresholds);
    auditService.log('UPDATE', 'TransactionMonitoring', { entity: 'Threshold', name: thresholds[idx].name, id });
    this.notifyListeners();
    return thresholds[idx];
  }

  async deleteThreshold(id) {
    const threshold = this.getThreshold(id);
    const thresholds = this.getThresholds().filter(t => t.id !== id);
    await this._dualWriteJson(this.THRESHOLDS_KEY, thresholds);
    if (threshold) {
      auditService.log('DELETE', 'TransactionMonitoring', { entity: 'Threshold', name: threshold.name, id });
    }
    this.notifyListeners();
  }

  // Returns { ruleId: [{ thresholdId, thresholdName, amount, currency }] } for AMOUNT-type rules
  // whose threshold exceeds an enabled regulatory threshold (same currency).
  getRuleMismatches() {
    const rules = this.getRules();
    const enabledThresholds = this.getThresholds().filter(t => t.enabled && t.amount != null);
    const mismatches = {};
    rules.forEach(rule => {
      if (rule.ruleType !== 'AMOUNT' || rule.threshold == null) return;
      const conflicting = enabledThresholds.filter(
        thr => thr.currency === rule.currency && rule.threshold > thr.amount
      );
      if (conflicting.length > 0) {
        mismatches[rule.id] = conflicting.map(thr => ({
          thresholdId: thr.id,
          thresholdName: thr.name,
          amount: thr.amount,
          currency: thr.currency,
        }));
      }
    });
    return mismatches;
  }

  // === ALERT PERFORMANCE DATA ===
  // Documents actual alarm performance from the operational monitoring system per rule and period.
  // This is documentation/risk assessment data, NOT operational alarm management.

  getAllAlertPerformance() {
    return storage.getJSON(this._entityKey(this.ALERT_PERFORMANCE_KEY), []);
  }

  getAlertPerformanceForRule(ruleId) {
    return this.getAllAlertPerformance().filter(p => p.ruleId === ruleId);
  }

  async saveAlertPerformance(data) {
    const all = this.getAllAlertPerformance();
    const existing = data.id ? all.findIndex(p => p.id === data.id) : -1;
    const entry = {
      ...data,
      id: data.id || `PERF-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      updatedAt: new Date().toISOString(),
    };
    if (existing >= 0) {
      all[existing] = { ...all[existing], ...entry };
    } else {
      all.push(entry);
    }
    await this._dualWriteJson(this.ALERT_PERFORMANCE_KEY, all);
    auditService.log('UPDATE', 'TransactionMonitoring', { entity: 'AlertPerformance', ruleId: data.ruleId, period: data.period });
    this.notifyListeners();
    return entry;
  }

  async deleteAlertPerformance(id) {
    const all = this.getAllAlertPerformance().filter(p => p.id !== id);
    await this._dualWriteJson(this.ALERT_PERFORMANCE_KEY, all);
    this.notifyListeners();
  }

  // === STATS ===

  getStats() {
    const rules = this.getRules();
    const performance = this.getAllAlertPerformance();
    const rulesWithPerformance = new Set(performance.map(p => p.ruleId));
    const mappedRules = rules.filter(r => (r.declaredRisks || []).length > 0);

    return {
      activeRules: rules.filter(r => r.enabled).length,
      totalRules: rules.length,
      mappedRules: mappedRules.length,
      rulesWithPerformance: rulesWithPerformance.size,
      totalThresholds: this.getThresholds().length,
      totalModels: this.getModels().length,
    };
  }

  // === MODELS ===

  getModels() {
    return storage.getJSON(this._entityKey(this.MODELS_KEY), []);
  }

  getModel(id) {
    return this.getModels().find(m => m.id === id) || null;
  }

  async addModel(model) {
    const models = this.getModels();
    const maxNum = models.reduce((max, m) => {
      const match = m.id && m.id.match(/^TXMOD-(\d+)$/);
      const num = match ? parseInt(match[1], 10) : 0;
      return num > max ? num : max;
    }, 0);
    const newModel = {
      ...model,
      id: `TXMOD-${String(maxNum + 1).padStart(3, '0')}`,
      status: model.status || 'draft',
      linkedRules: model.linkedRules || [],
      declaredRisks: model.declaredRisks || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    models.push(newModel);
    await this._dualWriteJson(this.MODELS_KEY, models);
    auditService.log('CREATE', 'TransactionMonitoring', { entity: 'Model', name: newModel.name, id: newModel.id });
    this.notifyListeners();
    return newModel;
  }

  async updateModel(id, updates) {
    const models = this.getModels();
    const idx = models.findIndex(m => m.id === id);
    if (idx < 0) return null;
    models[idx] = {
      ...models[idx],
      ...updates,
      id: models[idx].id,
      createdAt: models[idx].createdAt,
      updatedAt: new Date().toISOString(),
    };
    await this._dualWriteJson(this.MODELS_KEY, models);
    auditService.log('UPDATE', 'TransactionMonitoring', { entity: 'Model', name: models[idx].name, id });
    this.notifyListeners();
    return models[idx];
  }

  async deleteModel(id) {
    const model = this.getModel(id);
    const models = this.getModels().filter(m => m.id !== id);
    await this._dualWriteJson(this.MODELS_KEY, models);
    if (model) {
      auditService.log('DELETE', 'TransactionMonitoring', { entity: 'Model', name: model.name, id });
    }
    this.notifyListeners();
  }

  // Returns { riskId: [{ id, name }] } — which BWRA risks each model declares it addresses
  getModelDeclaredRiskCoverage() {
    const models = this.getModels();
    const coverage = {};
    models.forEach(model => {
      const risks = model.declaredRisks || [];
      risks.forEach(riskId => {
        if (!coverage[riskId]) coverage[riskId] = [];
        coverage[riskId].push({ id: model.id, name: model.name, status: model.status });
      });
    });
    return coverage;
  }

  // === VALIDATION ===

  getValidationResults() {
    return storage.getJSON(this._entityKey(this.VALIDATION_KEY), []);
  }

  getValidationHistoryLimit() {
    const settings = settingsService.getSettings();
    return settings.transactionMonitoring?.validationHistoryLimit ?? 100;
  }

  async saveValidationResult(result) {
    const all = this.getValidationResults();
    const entry = {
      ...result,
      id: `VAL-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      savedAt: new Date().toISOString(),
    };
    all.unshift(entry);
    const limit = this.getValidationHistoryLimit();
    await this._dualWriteJson(this.VALIDATION_KEY, all.slice(0, limit));
    auditService.log('CREATE', 'TransactionMonitoring', { entity: 'ValidationResult', id: entry.id });
    this.notifyListeners();
    return entry;
  }

  async deleteValidationResult(id) {
    const all = this.getValidationResults().filter(v => v.id !== id);
    await this._dualWriteJson(this.VALIDATION_KEY, all);
    this.notifyListeners();
  }

  // Returns a CSV string for the given validation result.
  // result: { id?, savedAt?, testedAt?, ruleResults: [{ ruleId, ruleName, ruleType, action, skipped, hits, misses }], summary }
  exportValidationResultToCSV(result) {
    const timestamp = result.savedAt || result.testedAt || new Date().toISOString();
    const id = result.id || 'UNSAVED';
    const csvEscape = (val) => {
      const s = String(val ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const rows = [
      ['Validation ID', 'Timestamp', 'Rule ID', 'Rule Name', 'Rule Type', 'Action', 'Transaction ID', 'Result', 'Detail'],
    ];
    const ruleResults = result.ruleResults || [];
    ruleResults.forEach(rr => {
      if (rr.skipped) {
        rows.push([id, timestamp, rr.ruleId, rr.ruleName, rr.ruleType, rr.action || '', '', 'SKIPPED', '']);
        return;
      }
      (rr.hits || []).forEach(h => {
        rows.push([id, timestamp, rr.ruleId, rr.ruleName, rr.ruleType, rr.action || '', h.txId, 'HIT', h.detail || '']);
      });
      (rr.misses || []).forEach(m => {
        rows.push([id, timestamp, rr.ruleId, rr.ruleName, rr.ruleType, rr.action || '', m.txId, 'MISS', m.detail || '']);
      });
      if ((rr.hits || []).length === 0 && (rr.misses || []).length === 0) {
        rows.push([id, timestamp, rr.ruleId, rr.ruleName, rr.ruleType, rr.action || '', '', 'NO_TRANSACTIONS', '']);
      }
    });
    return rows.map(row => row.map(csvEscape).join(',')).join('\r\n');
  }

  // Returns { ruleId: number } — total hits per rule from validation runs in the last `days` days.
  getTriggerCountsForRules(days = 30) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffIso = cutoff.toISOString();

    const validations = this.getValidationResults().filter(v => v.savedAt >= cutoffIso);
    const counts = {};
    validations.forEach(v => {
      const ruleResults = v.ruleResults || [];
      ruleResults.forEach(rr => {
        if (!rr.ruleId) return;
        const hits = Array.isArray(rr.hits) ? rr.hits.length : 0;
        counts[rr.ruleId] = (counts[rr.ruleId] || 0) + hits;
      });
    });
    return counts;
  }

  // Pure computation — runs selected rules against test transactions.
  // Returns { ruleResults: [...], summary: { totalHits, totalMisses, totalTransactions, rulesWithHits, totalRules } }
  runValidation(testTransactions, ruleIds) {
    const rules = ruleIds && ruleIds.length > 0
      ? this.getRules().filter(r => ruleIds.includes(r.id))
      : this.getRules();

    const ruleResults = rules.map(rule => {
      if (!rule.enabled) {
        return {
          ruleId: rule.id,
          ruleName: rule.name,
          ruleType: rule.ruleType,
          threshold: rule.threshold,
          currency: rule.currency,
          action: rule.action,
          skipped: true,
          hits: [],
          misses: [],
        };
      }

      const hits = [];
      const misses = [];
      testTransactions.forEach((tx, idx) => {
        const evaluation = this._evaluateRule(rule, tx);
        const txId = tx.id || tx.ID || String(idx + 1);
        if (evaluation.hit) {
          hits.push({ txId, detail: evaluation.detail });
        } else {
          misses.push({ txId, detail: evaluation.detail });
        }
      });

      return {
        ruleId: rule.id,
        ruleName: rule.name,
        ruleType: rule.ruleType,
        threshold: rule.threshold,
        currency: rule.currency,
        action: rule.action,
        skipped: false,
        hits,
        misses,
      };
    });

    const totalHits = ruleResults.reduce((sum, r) => sum + r.hits.length, 0);
    const totalMisses = ruleResults.reduce((sum, r) => sum + r.misses.length, 0);
    const rulesWithHits = ruleResults.filter(r => r.hits.length > 0).length;

    return {
      ruleResults,
      summary: {
        totalHits,
        totalMisses,
        totalTransactions: testTransactions.length,
        rulesWithHits,
        totalRules: rules.length,
      },
    };
  }

  // === RULE OVERLAP ANALYSIS ===

  // Returns an array of overlap pairs between rules.
  // Each pair: { a: rule, b: rule, overlaps: [{ type, detail }], isRedundant: bool }
  // Overlap types:
  //   REDUNDANT      - same ruleType, same currency, thresholds within 1% of each other
  //   AMOUNT_OVERLAP - both AMOUNT rules in same currency, both would trigger above the higher threshold
  //   RISK_OVERLAP   - both rules declare at least one shared BWRA risk
  //   FREQUENCY_OVERLAP - both FREQUENCY rules (monitoring same pattern class)
  getRuleOverlaps() {
    const rules = this.getRules();
    const pairs = [];

    for (let i = 0; i < rules.length; i++) {
      for (let j = i + 1; j < rules.length; j++) {
        const a = rules[i];
        const b = rules[j];
        const overlaps = [];

        // Amount/Frequency type overlap
        if (a.ruleType === b.ruleType) {
          if (a.ruleType === 'AMOUNT' && a.currency === b.currency && a.threshold != null && b.threshold != null) {
            const maxThr = Math.max(a.threshold, b.threshold);
            const ratio = Math.abs(a.threshold - b.threshold) / maxThr;
            if (ratio < 0.01) {
              overlaps.push({ type: 'REDUNDANT', detail: `${maxThr.toLocaleString()} ${a.currency}` });
            } else {
              overlaps.push({ type: 'AMOUNT_OVERLAP', detail: `${Math.max(a.threshold, b.threshold).toLocaleString()} ${a.currency}` });
            }
          } else if (a.ruleType === 'FREQUENCY') {
            overlaps.push({ type: 'FREQUENCY_OVERLAP', detail: '' });
          } else if (a.ruleType === 'PATTERN') {
            // Check if patterns share keywords
            const patA = (a.pattern || a.name || '').toLowerCase();
            const patB = (b.pattern || b.name || '').toLowerCase();
            const wordsA = patA.split(/[\W|]+/).filter(w => w.length > 3);
            const shared = wordsA.filter(w => patB.includes(w));
            if (shared.length > 0) {
              overlaps.push({ type: 'PATTERN_OVERLAP', detail: shared.slice(0, 3).join(', ') });
            }
          }
        }

        // Risk overlap
        const aRisks = new Set(a.declaredRisks || []);
        const bRisks = b.declaredRisks || [];
        const sharedRisks = bRisks.filter(r => aRisks.has(r));
        if (sharedRisks.length > 0) {
          overlaps.push({ type: 'RISK_OVERLAP', sharedCount: sharedRisks.length, sharedRisks });
        }

        if (overlaps.length > 0) {
          pairs.push({
            a,
            b,
            overlaps,
            isRedundant: overlaps.some(o => o.type === 'REDUNDANT'),
          });
        }
      }
    }

    return pairs;
  }

  // === ALERT CASES ===
  // Operational case management for alerts: New → Under Investigation → SAR Report / Closed

  getAlertCases() {
    return storage.getJSON(this._entityKey(this.ALERT_CASES_KEY), []);
  }

  getAlertCase(id) {
    return this.getAlertCases().find(a => a.id === id) || null;
  }

  _generateAlertCaseId() {
    const alerts = this.getAlertCases();
    const maxNum = alerts.reduce((max, a) => {
      const match = a.id && a.id.match(/^ALT-(\d+)$/);
      const num = match ? parseInt(match[1], 10) : 0;
      return num > max ? num : max;
    }, 0);
    return `ALT-${String(maxNum + 1).padStart(3, '0')}`;
  }

  async createAlertCase(data, currentUser) {
    const alerts = this.getAlertCases();
    const userId = currentUser?.username || currentUser?.id || 'system';
    const newAlert = {
      id: this._generateAlertCaseId(),
      ruleId: data.ruleId || null,
      ruleName: data.ruleName || '',
      subject: data.subject || '',
      description: data.description || '',
      amount: data.amount != null ? data.amount : null,
      currency: data.currency || '',
      status: 'new',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      auditTrail: [{
        timestamp: new Date().toISOString(),
        userId,
        fromStatus: null,
        toStatus: 'new',
        comment: data.comment || '',
      }],
    };
    alerts.push(newAlert);
    await this._dualWriteJson(this.ALERT_CASES_KEY, alerts);
    auditService.log('CREATE', 'TransactionMonitoring', { entity: 'AlertCase', id: newAlert.id });
    this.notifyListeners();
    return newAlert;
  }

  async updateAlertCaseStatus(id, newStatus, comment, currentUser) {
    const alerts = this.getAlertCases();
    const idx = alerts.findIndex(a => a.id === id);
    if (idx < 0) return null;
    const userId = currentUser?.username || currentUser?.id || 'system';
    const old = alerts[idx];
    alerts[idx] = {
      ...old,
      status: newStatus,
      updatedAt: new Date().toISOString(),
      auditTrail: [
        ...(old.auditTrail || []),
        {
          timestamp: new Date().toISOString(),
          userId,
          fromStatus: old.status,
          toStatus: newStatus,
          comment: comment || '',
        },
      ],
    };
    await this._dualWriteJson(this.ALERT_CASES_KEY, alerts);
    auditService.log('UPDATE', 'TransactionMonitoring', { entity: 'AlertCase', id, fromStatus: old.status, toStatus: newStatus });
    this.notifyListeners();
    return alerts[idx];
  }

  async deleteAlertCase(id) {
    const alert = this.getAlertCase(id);
    const alerts = this.getAlertCases().filter(a => a.id !== id);
    await this._dualWriteJson(this.ALERT_CASES_KEY, alerts);
    if (alert) {
      auditService.log('DELETE', 'TransactionMonitoring', { entity: 'AlertCase', id });
    }
    this.notifyListeners();
  }

  getAlertCaseStats() {
    const alerts = this.getAlertCases();
    return {
      total: alerts.length,
      new: alerts.filter(a => a.status === 'new').length,
      investigating: alerts.filter(a => a.status === 'investigating').length,
      sar: alerts.filter(a => a.status === 'sar').length,
      closed: alerts.filter(a => a.status === 'closed').length,
    };
  }

  // Returns performance metrics for a specific rule, computed from alert cases and documented performance data.
  // Designed to be replaced with real data from a connected monitoring system.
  getRulePerformanceMetrics(ruleId) {
    const allCases = this.getAlertCases().filter(c => c.ruleId === ruleId);
    const perfData = this.getAlertPerformanceForRule(ruleId);

    // From alert cases: resolved = sar or closed
    const sarCases = allCases.filter(c => c.status === 'sar').length;
    const closedCases = allCases.filter(c => c.status === 'closed').length;
    const resolvedCases = sarCases + closedCases;
    const caseFpRate = resolvedCases > 0 ? closedCases / resolvedCases : null;
    const caseDetectionRate = resolvedCases > 0 ? sarCases / resolvedCases : null;

    // Triggers per day and week from alert cases in last 30 days
    const cutoff30 = new Date();
    cutoff30.setDate(cutoff30.getDate() - 30);
    const cutoff7 = new Date();
    cutoff7.setDate(cutoff7.getDate() - 7);
    const recent30 = allCases.filter(c => new Date(c.createdAt) >= cutoff30).length;
    const recent7 = allCases.filter(c => new Date(c.createdAt) >= cutoff7).length;
    const triggersPerDay = Math.round((recent30 / 30) * 10) / 10;
    const triggersPerWeek = Math.round((recent7) * 10) / 10;

    // From documented performance data (AlertPerformanceTab entries)
    let perfTotalCreated = 0;
    let perfTotalSar = 0;
    perfData.forEach(p => {
      if (p.alertsCreated != null) perfTotalCreated += p.alertsCreated;
      if (p.sarFiled != null) perfTotalSar += p.sarFiled;
    });
    const perfFpRate = perfTotalCreated > 0 ? (perfTotalCreated - perfTotalSar) / perfTotalCreated : null;
    const perfDetectionRate = perfTotalCreated > 0 ? perfTotalSar / perfTotalCreated : null;

    // Use documented data if available, else fall back to case-derived metrics
    const fpRate = perfFpRate !== null ? perfFpRate : caseFpRate;
    const detectionRate = perfDetectionRate !== null ? perfDetectionRate : caseDetectionRate;
    const dataSource = perfFpRate !== null ? 'documented' : (caseFpRate !== null ? 'cases' : 'none');

    return {
      totalCases: allCases.length,
      resolvedCases,
      sarCases,
      closedCases,
      fpRate,
      detectionRate,
      triggersLast30Days: recent30,
      triggersLast7Days: recent7,
      triggersPerDay,
      triggersPerWeek,
      hasPerfData: perfData.length > 0,
      perfTotalCreated,
      perfTotalSar,
      dataSource,
      highFpWarning: fpRate !== null && fpRate > 0.80,
    };
  }

  // Analyses rule performance metrics and suggests an optimised threshold value.
  // Returns a suggestion object; callers check canSuggest and suggestedThreshold.
  getThresholdSuggestions(ruleId) {
    const rule = this.getRule(ruleId);
    if (!rule) return { canSuggest: false, reasonKey: 'noRule' };

    if (rule.ruleType === 'PATTERN') {
      return { canSuggest: false, reasonKey: 'patternRule', currentThreshold: null };
    }

    const metrics = this.getRulePerformanceMetrics(ruleId);
    const currentThreshold = rule.threshold;

    if (currentThreshold == null || currentThreshold <= 0) {
      return { canSuggest: false, reasonKey: 'noThreshold', currentThreshold, metrics };
    }

    if (metrics.dataSource === 'none') {
      return { canSuggest: false, reasonKey: 'noData', currentThreshold, metrics };
    }

    const { fpRate, detectionRate, triggersPerDay } = metrics;

    let suggestedThreshold = null;
    let reasonKey = null;
    let expectedFpChange = null;
    let expectedDetectionChange = null;

    if (fpRate !== null && fpRate > 0.80) {
      // Critical false-positive rate: raise threshold significantly
      suggestedThreshold = Math.round(currentThreshold * 1.35);
      reasonKey = 'highFpCritical';
      expectedFpChange = -0.20;
      expectedDetectionChange = -0.05;
    } else if (fpRate !== null && fpRate > 0.60) {
      // Elevated false-positive rate: moderate threshold increase
      suggestedThreshold = Math.round(currentThreshold * 1.20);
      reasonKey = 'highFpModerate';
      expectedFpChange = -0.10;
      expectedDetectionChange = -0.02;
    } else if (triggersPerDay > 20) {
      // Very high trigger volume: raise threshold to reduce analyst load
      suggestedThreshold = Math.round(currentThreshold * 1.30);
      reasonKey = 'highTriggerRate';
      expectedFpChange = -0.15;
      expectedDetectionChange = -0.03;
    } else if (detectionRate !== null && detectionRate < 0.30 && triggersPerDay > 0.5) {
      // Low detection despite sufficient trigger volume: lower threshold
      suggestedThreshold = Math.round(currentThreshold * 0.80);
      reasonKey = 'lowDetection';
      expectedFpChange = 0.10;
      expectedDetectionChange = 0.15;
    } else if (triggersPerDay > 0 && triggersPerDay < 0.5) {
      // Almost no triggers: threshold may be too high
      suggestedThreshold = Math.round(currentThreshold * 0.75);
      reasonKey = 'lowTriggerRate';
      expectedFpChange = 0.05;
      expectedDetectionChange = 0.10;
    } else {
      return {
        canSuggest: true,
        currentThreshold,
        suggestedThreshold: null,
        reasonKey: 'performanceGood',
        expectedFpChange: null,
        expectedDetectionChange: null,
        currency: rule.currency,
        metrics,
      };
    }

    return {
      canSuggest: true,
      currentThreshold,
      suggestedThreshold,
      currency: rule.currency,
      reasonKey,
      expectedFpChange,
      expectedDetectionChange,
      changePercent: Math.round(((suggestedThreshold - currentThreshold) / currentThreshold) * 100),
      metrics,
    };
  }

  // === SCENARIO TESTING ===

  // Returns predefined realistic AML test scenarios with their transaction sets.
  getScenarios() {
    return [
      {
        id: 'structuring',
        riskLevel: 'high',
        transactions: [
          { id: 'STR-1', amount: 9900, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
          { id: 'STR-2', amount: 9800, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
          { id: 'STR-3', amount: 9750, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
          { id: 'STR-4', amount: 9900, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
          { id: 'STR-5', amount: 9850, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
        ],
      },
      {
        id: 'smurfing',
        riskLevel: 'high',
        transactions: [
          { id: 'SMF-1', amount: 3000, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-2', amount: 2500, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-3', amount: 2800, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-4', amount: 3200, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-5', amount: 2900, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-6', amount: 3100, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-7', amount: 2700, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-8', amount: 3300, currency: 'EUR', type: 'transfer', count: 1, description: 'betalning' },
          { id: 'SMF-AGG', amount: 25500, currency: 'EUR', type: 'aggregated', count: 8, description: 'sammanlagd aktivitet' },
        ],
      },
      {
        id: 'tbml',
        riskLevel: 'high',
        transactions: [
          { id: 'TBML-1', amount: 25000, currency: 'EUR', type: 'import', count: 1, description: 'iran elektronik faktura' },
          { id: 'TBML-2', amount: 18000, currency: 'EUR', type: 'export', count: 1, description: 'myanmar varor betalning' },
          { id: 'TBML-3', amount: 12000, currency: 'EUR', type: 'import', count: 1, description: 'nordkorea mineraler handel' },
        ],
      },
      {
        id: 'pep',
        riskLevel: 'medium',
        transactions: [
          { id: 'PEP-1', amount: 75000, currency: 'EUR', type: 'transfer', count: 1, description: 'pep politiker betalning donation' },
          { id: 'PEP-2', amount: 50000, currency: 'EUR', type: 'deposit', count: 1, description: 'minister konsultarvode' },
          { id: 'PEP-3', amount: 30000, currency: 'EUR', type: 'transfer', count: 1, description: 'diplomat honorar' },
        ],
      },
      {
        id: 'large_cash',
        riskLevel: 'medium',
        transactions: [
          { id: 'LC-1', amount: 75000, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
          { id: 'LC-2', amount: 50000, currency: 'EUR', type: 'deposit', count: 1, description: 'kontantinsättning' },
        ],
      },
    ];
  }

  // === IMPORT / EXPORT ===

  exportRulesToJSON(ruleIds) {
    const rules = ruleIds && ruleIds.length > 0
      ? this.getRules().filter(r => ruleIds.includes(r.id))
      : this.getRules();
    return {
      exportedAt: new Date().toISOString(),
      version: '1.0',
      rules: rules.map(r => ({
        id: r.id,
        name: r.name,
        description: r.description || '',
        systemName: r.systemName || '',
        ruleType: r.ruleType,
        threshold: r.threshold,
        currency: r.currency,
        action: r.action,
        enabled: r.enabled,
        pattern: r.pattern || '',
        declaredRisks: r.declaredRisks || [],
        linkedProducts: r.linkedProducts || [],
      })),
    };
  }

  analyzeImportConflicts(data) {
    if (!data || !Array.isArray(data.rules)) {
      return { valid: false, error: 'Invalid format: missing rules array' };
    }
    const existingRules = this.getRules();
    const existingIds = new Set(existingRules.map(r => r.id));
    const existingNames = new Set(existingRules.map(r => (r.name || '').toLowerCase()));
    const conflicts = [];
    const newRules = [];
    for (const rule of data.rules) {
      if (!rule.name?.trim()) continue;
      const idConflict = existingIds.has(rule.id);
      const nameConflict = existingNames.has((rule.name || '').toLowerCase());
      if (idConflict || nameConflict) {
        conflicts.push({ ...rule, conflictType: idConflict ? 'id' : 'name' });
      } else {
        newRules.push(rule);
      }
    }
    return { valid: true, total: data.rules.length, conflicts, newRules };
  }

  // conflictStrategy: 'skip' | 'overwrite' | 'new'
  async importRulesFromJSON(data, conflictStrategy = 'skip') {
    if (!data || !Array.isArray(data.rules)) {
      return { imported: 0, skipped: 0, overwritten: 0, errors: ['Invalid format: missing rules array'] };
    }
    const existingRules = this.getRules();
    const existingIds = new Set(existingRules.map(r => r.id));
    const existingNames = new Map(existingRules.map(r => [(r.name || '').toLowerCase(), r.id]));
    let imported = 0;
    let skipped = 0;
    let overwritten = 0;
    const errors = [];
    for (const rule of data.rules) {
      if (!rule.name?.trim()) {
        errors.push(`Skipped unnamed rule`);
        skipped++;
        continue;
      }
      const idConflict = existingIds.has(rule.id);
      const nameConflict = existingNames.has((rule.name || '').toLowerCase());
      const hasConflict = idConflict || nameConflict;
      if (hasConflict && conflictStrategy === 'skip') {
        skipped++;
        continue;
      }
      if (hasConflict && conflictStrategy === 'overwrite') {
        const targetId = idConflict ? rule.id : existingNames.get((rule.name || '').toLowerCase());
        await this.updateRule(targetId, {
          name: rule.name,
          description: rule.description || '',
          systemName: rule.systemName || '',
          ruleType: rule.ruleType,
          threshold: rule.threshold,
          currency: rule.currency,
          action: rule.action,
          enabled: rule.enabled,
          pattern: rule.pattern || '',
          declaredRisks: rule.declaredRisks || [],
          linkedProducts: rule.linkedProducts || [],
        });
        overwritten++;
        continue;
      }
      // 'new' strategy or no conflict: add as new rule (new ID generated)
      await this.addRule({
        name: rule.name,
        description: rule.description || '',
        systemName: rule.systemName || '',
        ruleType: rule.ruleType || 'AMOUNT',
        threshold: rule.threshold,
        currency: rule.currency || 'EUR',
        action: rule.action || 'ALERT',
        enabled: rule.enabled !== undefined ? rule.enabled : true,
        pattern: rule.pattern || '',
        declaredRisks: rule.declaredRisks || [],
        linkedProducts: rule.linkedProducts || [],
      });
      imported++;
    }
    return { imported, skipped, overwritten, errors };
  }

  // === RULE TEMPLATES ===

  getTemplates() {
    return [
      {
        id: 'tpl_ctr',
        category: 'ctr',
        ruleType: 'AMOUNT',
        threshold: 15000,
        currency: 'EUR',
        action: 'SAR',
        pattern: '',
      },
      {
        id: 'tpl_structuring',
        category: 'structuring',
        ruleType: 'FREQUENCY',
        threshold: 3,
        currency: 'EUR',
        action: 'REVIEW',
        pattern: '',
      },
      {
        id: 'tpl_highrisk',
        category: 'highrisk',
        ruleType: 'PATTERN',
        threshold: null,
        currency: 'EUR',
        action: 'ALERT',
        pattern: 'iran|nordkorea|myanmar|syria|kuba|libyen',
      },
      {
        id: 'tpl_pep',
        category: 'pep',
        ruleType: 'PATTERN',
        threshold: null,
        currency: 'EUR',
        action: 'REVIEW',
        pattern: 'pep|politiker|minister|senator|diplomat|statsminister',
      },
      {
        id: 'tpl_large',
        category: 'largeamount',
        ruleType: 'AMOUNT',
        threshold: 50000,
        currency: 'EUR',
        action: 'ALERT',
        pattern: '',
      },
    ];
  }

  _evaluateRule(rule, tx) {
    switch (rule.ruleType) {
      case 'AMOUNT': {
        const amount = parseFloat(tx.amount || tx.Amount || tx.belopp || 0);
        const threshold = parseFloat(rule.threshold);
        if (isNaN(threshold)) return { hit: false, detailKey: 'modules.transactionMonitoring.validation.noThresholdDefined' };
        const hit = amount >= threshold;
        const currency = tx.currency || rule.currency || '';
        return {
          hit,
          detail: `${amount} ${currency} ${hit ? '>=' : '<'} ${threshold}`,
        };
      }
      case 'FREQUENCY': {
        const count = parseInt(tx.count || tx.Count || tx.frequency || tx.antal || 0, 10);
        const threshold = parseFloat(rule.threshold);
        if (isNaN(threshold)) return { hit: false, detailKey: 'modules.transactionMonitoring.validation.noThresholdDefined' };
        const hit = count >= threshold;
        return {
          hit,
          detail: `count ${count} ${hit ? '>=' : '<'} ${threshold}`,
        };
      }
      case 'PATTERN': {
        const searchText = [
          tx.description, tx.type, tx.category, tx.note, tx.memo,
          tx.beskrivning, tx.typ, tx.kategori,
        ].filter(Boolean).join(' ');
        let hit = false;
        if (rule.pattern && rule.pattern.trim()) {
          try {
            hit = new RegExp(rule.pattern, 'i').test(searchText);
          } catch {
            hit = false;
          }
        } else {
          // Fallback: keyword match from rule name (legacy rules without pattern)
          const keywords = (rule.name || '').toLowerCase().split(/\s+/).filter(w => w.length > 3);
          hit = keywords.length > 0 && keywords.some(kw => searchText.toLowerCase().includes(kw));
        }
        return {
          hit,
          detailKey: hit ? 'modules.transactionMonitoring.validation.patternMatchFound' : 'modules.transactionMonitoring.validation.noPatternMatch',
        };
      }
      default:
        return { hit: false, detailKey: 'modules.transactionMonitoring.validation.unknownRuleType' };
    }
  }

  // INFRA-181: backend dual-write + initFromBackend.
  async initFromBackend() {
    return this._initFromBackend(
      [this.RULES_KEY, this.RULE_VERSIONS_KEY, this.THRESHOLDS_KEY,
       this.ALERT_PERFORMANCE_KEY, this.MODELS_KEY, this.VALIDATION_KEY,
       this.ALERT_CASES_KEY],
      [],
    );
  }
}

applyDualWriteMixin(TransactionMonitoringService, makeDualWriteMixin({
  storage, api: txmonApi, serviceName: 'TransactionMonitoringService',
}));

const transactionMonitoringService = new TransactionMonitoringService();
export default transactionMonitoringService;
